import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  FormControl,
  FormLabel,
  Grid,
  OutlinedInput,
  Stack,
} from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ConditionalRenderer,
  useLanguageTranslator,
} from "../../../../../components";
import { useAuthSession } from "../../../../../components/AuthSessionProvider";
import { useNotify } from "../../../../../components/@common/NotificationsProvider";
import { toastHandler } from "../../../../../utils/helpers";
import { toastTypesKeys } from "../../../../../utils/constants";
import { ToastContainer } from "react-toastify";

const CREATE_PRACTICE_CATEGORY = gql`
  mutation CreatePracticeCategory($input: PracticeCategoryCreateInput!) {
    practiceCategoryCreate(input: $input) {
      clientMutationId
      practiceCategory {
        id
        name
        createdAt
        updatedAt
      }
    }
  }
`;

const GET_PRACTICECATEGORY = gql`
  query GetPracticeCategory($id: ID!) {
    practiceCategory(id: $id) {
      id
      name
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_PRACTICECATEGORY = gql`
  mutation UpdatePracticeCategory($id: ID!, $name: String!) {
    practiceCategoryUpdate(
      input: { id: $id, practiceCategoryInput: { name: $name } }
    ) {
      clientMutationId
      practiceCategory {
        id
        name
        createdAt
        updatedAt
      }
    }
  }
`;

function PracticeCategoriesAction({ isUpdate = false }) {
  const { user } = useAuthSession() as any;
  const notify = useNotify();
  const navigate = useNavigate();
  const { translator, currentLanguage } = useLanguageTranslator();

  const [searchParams] = useSearchParams();
  const practice_category_id = searchParams.get("id");

  const { data: practiceCategoryData } = useQuery(GET_PRACTICECATEGORY, {
    variables: { id: practice_category_id },
    skip: !practice_category_id,
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const [createPracticeCategories, { loading, error }] = useMutation(
    CREATE_PRACTICE_CATEGORY,
    {
      context: {
        headers: {
          "accept-language": currentLanguage || "en",
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  const [updatePracticeCategory] = useMutation(UPDATE_PRACTICECATEGORY, {
    context: {
      headers: {
        "accept-language": currentLanguage || "en",
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const initialValues: any = useMemo(() => {
    if (!practiceCategoryData?.practiceCategory) return undefined;

    const { name } = practiceCategoryData?.practiceCategory;

    return {
      practices_category_name: name,
    };
  }, [practiceCategoryData]);

  const form = useForm<any>({
    defaultValues: {
      practices_category_name: "",
    },
    values: initialValues,
  });

  const handleFinish = useCallback(async (data: any, actionType: any) => {
    if (actionType === "update") {
      try {
        const response = await updatePracticeCategory({
          variables: {
            id: Number(practice_category_id),
            name: data?.practices_category_name,
          },
        });
        if (response.data) {
          notify.success(`Practice Category Updated successfuly!`);
          setTimeout(() => {
            navigate("/admin/practice-categories");
            return;
          }, 1000);
        }
      } catch (err) {}
      return;
    }

    try {
      const response = await createPracticeCategories({
        variables: {
          input: {
            practiceCategoryInput: { name: data?.practices_category_name },
          },
        },
      });
      if (actionType === "save") {
        if (response.data) {
          notify.success(`Practice Category Created successfuly!`);
          setTimeout(() => {
            navigate("/admin/practice-categories");
            return;
          }, 1000);
        }
      } else if (actionType === "saveAndAddNew") {
        if (response.data) {
          notify.success(`Practice Category Created successfuly!`);
          setTimeout(() => {
            form?.reset();
            return;
          }, 1000);
        }
      }
    } catch (err) {
      const errorCatch = JSON.stringify(err);
      const parsedError = JSON.parse(errorCatch);

      toastHandler(parsedError?.message || "", toastTypesKeys.error);
    }
  }, []);

  return (
    <>
      <ToastContainer />
      <form onSubmit={form.handleSubmit((data) => handleFinish(data, "save"))}>
        <Grid
          container
          px={4}
          sx={{ backgroundColor: "#ffffff", borderRadius: "10px" }}
          spacing={2}
        >
          <Grid item xs={12} sm={6} lg={6}>
            <FormControl required disabled={false} fullWidth margin="normal">
              <FormLabel>
                {translator("Practice Category")}{" "}
                {translator("dashboardTableHead.Name")}
              </FormLabel>
              <OutlinedInput
                {...form?.register("practices_category_name", {
                  required: "Required!",
                })}
              />
              {/* <FormHelperText>{errors.title?.message}</FormHelperText> */}
            </FormControl>
          </Grid>
        </Grid>
        <Stack
          sx={{ backgroundColor: "#ffffff" }}
          direction={"row"}
          justifyContent={"flex-end"}
          gap={2}
          px={4}
          py={4}
        >
          <Link
            to="/admin/practice-categories"
            onClick={() => form?.reset()}
            style={{
              backgroundColor: "#e0e0e0",
              padding: "8px 12px",
              color: "#989C9F",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {translator("Cancel")}
          </Link>
          <Button
            type="button"
            onClick={form.handleSubmit((data) =>
              handleFinish(data, isUpdate ? "update" : "save")
            )}
            style={{
              backgroundColor: "#00AAB0",
              padding: "8px 12px",
              color: "white",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {isUpdate
              ? translator("generic.Update")
              : translator("generic.Save")}
          </Button>
          <ConditionalRenderer condition={!isUpdate}>
            <Button
              type="button"
              onClick={form.handleSubmit((data) =>
                handleFinish(data, "saveAndAddNew")
              )}
              style={{
                backgroundColor: "#F6A000",
                padding: "8px 12px",
                color: "white",
                borderRadius: "10px",
                fontSize: "14px",
                textDecoration: "none",
              }}
            >
              {translator("generic.Save")} & {translator("generic.Add New")}
            </Button>
          </ConditionalRenderer>
        </Stack>
      </form>
    </>
  );
}

export { PracticeCategoriesAction };
